import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { fadeUp, viewportOnce } from './Animations';
import type { IitOutcomesData } from '@/lib/iit-outcomes';

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// Order in data.employment should be [Employed full-time, Seeking, ...others].
// The first entry drives the hero stat below.
const BAR_PALETTE = ['#12539F', '#EDA130', '#C5DAF1', '#0B3A73', '#F5C77E'];

export default function OutcomesCharts({ data }: { data: IitOutcomesData }) {
  const employment = data.employment;
  const total = useMemo(
    () => employment.reduce((sum, e) => sum + e.percent, 0),
    [employment],
  );
  const headline = employment[0];

  return (
    <section id="outcomes" className="px-6 pt-16 pb-10 sm:px-10 sm:pt-24 sm:pb-14 lg:px-16">
      <div className="container-content">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-edept-orange">
            Graduate outcomes
          </p>
          <h2 className="mt-3 text-3xl text-edept-dark sm:text-4xl">
            Where Illinois Tech graduates land
          </h2>
          <p className="mt-4 text-slate-600">
            {data.classYear} &middot; Advanced-degree (Graduate College) outcomes, Illinois Tech
            Career Services.{' '}
            <a
              href={data.sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-edept-dark underline underline-offset-2"
            >
              View the original dataset
            </a>
            .
          </p>
          {data.sourceNote && <p className="mt-2 text-xs text-slate-400">{data.sourceNote}</p>}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]"
        >
          {/* Employment status — proportional "runway strip", no chart library */}
          <div className="rounded-3xl border border-edept-light-gray p-6 shadow-card sm:p-10">
            <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">
              Employment status
            </p>

            {headline && (
              <p className="mt-3 font-heading text-6xl text-edept-dark">
                {headline.percent}%
                <span className="ml-2 align-middle text-lg font-normal text-slate-500">
                  {headline.status.toLowerCase()}
                </span>
              </p>
            )}

            <div className="mt-8 flex h-4 w-full overflow-hidden rounded-full bg-edept-light-gray">
              {employment.map((e, i) => (
                <motion.div
                  key={e.status}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(e.percent / total) * 100}%` }}
                  viewport={viewportOnce}
                  transition={{ duration: 0.8, delay: i * 0.15, ease: 'easeOut' }}
                  style={{ backgroundColor: BAR_PALETTE[i % BAR_PALETTE.length] }}
                />
              ))}
            </div>

            <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
              {employment.map((e, i) => (
                <li key={e.status} className="flex items-center gap-2 text-sm text-slate-600">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: BAR_PALETTE[i % BAR_PALETTE.length] }}
                  />
                  {e.status} <span className="font-semibold text-edept-dark">{e.percent}%</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Salary — dark panel, no chart */}
          <div className="rounded-3xl bg-edept-dark p-6 text-white shadow-card sm:p-10">
            {data.salarySummary ? (
              <>
                <p className="text-sm font-semibold uppercase tracking-widest text-edept-light-blue">
                  Starting salary
                </p>
                <p className="mt-3 font-heading text-5xl">
                  {currencyFmt.format(data.salarySummary.median)}
                </p>
                <p className="mt-1 text-sm text-edept-light-blue">Median</p>

                <div className="mt-6 border-t border-white/15 pt-6">
                  <p className="font-heading text-3xl text-edept-orange">
                    {currencyFmt.format(data.salarySummary.mean)}
                  </p>
                  <p className="mt-1 text-sm text-edept-light-blue">Mean</p>
                </div>

                <p className="mt-6 text-xs text-white/60">{data.salarySummary.note}</p>
              </>
            ) : (
              <p className="py-16 text-center text-sm text-white/70">
                Salary data not available.
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
